<template>

  <div class="overflow-visible w-full mx-4 2xl:mx-0 2xl:w-4/5 pt-6">
    <report-preview
      v-model:show="showDetails"
      :report-history="selectedHistory">
    </report-preview>

    <report-preview
        v-model:show="showConfirm"
        :report-history="selectedReportHistory"
        :selected-review-type="getReviewType()"
        :is-approval="true"
        @approve-report="approveReport()"
    >
    </report-preview>

    <h2>Report Histories</h2>


    <!-- Filters for the Report Histpry Table based on Groups, Templates, or Report Types -->
    <div class="flex">
      <div class="pt-2 w-1/5 px-1">
        <label class="block text-sm font-medium text-gray-700">Filter By Groups</label>
        <select2
            v-model:value="selectedGroups"
            v-model:options="currentGroupOptions"
            :multiple="true"
            name="groups"
            @update:value="updateSelectedGroup()"
        >
        </select2>
      </div>

      <div class="pt-2 w-1/5 px-1">
        <label class="block text-sm font-medium text-gray-700">Filter By Templates</label>
        <select2
            v-model:value="selectedReportTemplates"
            v-model:options="currentTemplateOptions"
            :multiple="true"
            name="templates"
            @update:value="updateSelectedTemplate()"
        >
        </select2>
      </div>

      <div class="pt-2 pb-2 w-1/5 px-1">
        <label class="block text-sm font-medium text-gray-700">Filter By Report Type</label>
        <select2
            v-model:value="selectedReportTypes"
            v-model:options="currentReportOptions"
            :multiple="true"
            name="reportTypes"
            @update:value="updateSelectedReport()"
        >
        </select2>
      </div>
      </div>
      <div class="flex pb-1">
        <div class="px-1">
      <button-green-primary @click="updateFilters()" class="pt-2 pb-2"
      >
        <div v-if="processing"
             class="inline-flex">
          <span class="lnr icon-loading animate-spin" style="font-size: 1.2rem;"></span>
          <span class="pl-4">Processing</span>
        </div>
        <div v-else
             class="inline-flex">
          <span>Update Filters</span>
        </div>

      </button-green-primary>
          </div>
        <div class="px-1">

      <button-red-secondary @click="clearFilters()" class="pt-2 pb-2"
      >
        <div v-if="processing"
             class="inline-flex">
          <span class="lnr icon-loading animate-spin" style="font-size: 1.2rem;"></span>
          <span class="pl-4">Processing</span>
        </div>
        <div v-else
             class="inline-flex">
          <span>Clear Filters</span>
        </div>

    </button-red-secondary>
          </div>
        </div>



    <!-- Table -->
    <ajax-table :data="data"
                :headers="headers"
                v-model:loading="ajaxTable.loading"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                v-model:page="ajaxTable.page"
                v-model:items-per-page="ajaxTable.itemsPerPage"
                v-model:page-info="ajaxTable.pageInfo"
                :exportable="false"
                :search-enabled="false"
                @loadData="getData"
                class="overflow-visible -mt-16"
                @rowSelected="showDetailsPanel"
    >

      <!-- Column 1: Report Information -->
      <template #col-1="props">
        <div    class="flex flex-col items-start justify-center">
                      <div class="sm:col-span-1 pb-2">
                        <dt class="font-medium text-gray-500">Report Type</dt>
                        <dd class="text-gray-900">{{ props.row.report_type_pretty }}</dd>
                      </div>
                      <div v-if="props.row.report_template != null" class="sm:col-span-1 pb-2">
                        <dt class="font-medium text-gray-500">Report Template</dt>
                        <dd class="text-gray-900">{{ props.row.report_template.template_name }}</dd>
                        </div>

        </div>
      </template>

      <!-- Column 2: Report Generation Info -->
      <template #col-2="props">
        <div    class="grid grid-cols-1">
          <div v-if="props.row.report_group != null" class="pb-2">
            <dt class="font-medium text-gray-500">Group</dt>
            <dd class="text-gray-900">{{ props.row.report_group.name }}</dd>
          </div>
          <div class="pb-2 pr-6">
            <dt class="font-medium text-gray-500">Generated By</dt>
            <dd v-if="props.row.generated_by_user !== null" class="text-gray-900">{{ props.row.generated_by_user.display_name }}</dd>
            <dd v-else class="text-gray-900">SoniCloud</dd>
          </div>
          <div class="pb-2 w-full">
            <dt class="font-medium text-gray-500">Sent On</dt>
            <dd class="text-gray-900">{{ props.row.sent_on_pretty }}</dd>
          </div>

        </div>
      </template>

      <!-- Column 3: Reviewed By -->
      <template #col-3="props">
        <div    v-if="props.row.cloud_file_path != null"
                class="grid grid-cols-1">
          <div v-if="(props.row.reviewed_by_users.length > 0)">
            <div v-for="user in props.row.reviewed_by_users">

                        <div class="block items-center m-1 px-2.5 py-0.5 rounded-md text-md font-medium bg-blue-100 text-blue-800 ">
                            <div>{{user.display_name }}</div>
                          <div class="text-sm">Reviewed {{ user.createdAtDiff  }}</div>
                        </div>
          </div>
          </div>
          <div>
          <div v-for="user in props.row.approved_by_users">
            <div class="block items-center m-1 px-2.5 py-0.5 rounded-md text-md font-medium bg-green-100 text-green-800">
              <div>{{user.display_name }}</div>
              <div class="text-sm">Approved {{ user.createdAtDiff  }}</div>
            </div>
          </div>
            </div>
        </div>
      </template>


      <!-- Column 4: Actions -->
      <template #col-4="props" class="overflow-visible">
        <div    v-if="props.row.cloud_file_path != null">
            <Menu   v-if="featureLevel.hasFeature(Features.REPORT_APPROVALS, site)"
                    as="div"
                    class="float-left text-left"
                    @click.stop>
                <div @click.stop>
                    <MenuButton class="inline-flex w-full justify-end rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100" @click.stop>
                        Sign Report
                        <div class="h-6 w-6 mr-3">
                            <span class="lnr icon-chevron-down mr-1 ml-4 h-4 w-4"></span>
                        </div>
                    </MenuButton>
                </div>
                <MenuItems class="overflow-visible absolute mt-2 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div class="py-1">
                        <MenuItem v-slot="{ active }">
                                        <span
                                            @click.stop="showConfirmModal('review', props.row.id, props.row.cloud_file_path, props.row)"
                                            :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm infline-flex pl-4']"
                                        >
                                          <span class="lnr icon-clipboard-text pr-2"></span>
                                          Sign as Reviewer...
                                        </span>
                        </MenuItem>
                        <MenuItem v-slot="{ active }">
                                        <span
                                            @click.stop="showConfirmModal('approve', props.row.id, props.row.cloud_file_path, props.row)"
                                            :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm infline-flex pl-4']"
                                        >
                                          <span class="lnr icon-clipboard-check pr-2"></span>
                                            Sign as Approver...
                                        </span>
                        </MenuItem>
                    </div>
                </MenuItems>
                <!--                                                </transition>-->
            </Menu>
            <FeatureGate v-else
                         feature-name="Report Review & Approvals"
            ></FeatureGate>
        </div>
        <div    v-else>
            <div    class="px-3 py-2 bg-yellow-50 text-yellow-700 rounded-md">
                <span>No report generated for interval</span>
            </div>
        </div>
      </template>
    </ajax-table>
  </div>
</template>

<script>

import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Pagination from "@/components/Pagination"
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect"
import AjaxTable from "@/components/elements/AjaxTable";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import Loading from "@/components/elements/loading";
import {UrlParamsMixin} from "@/mixins/UrlParamsMixin";
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import NotificationModal from "@/components/elements/NotificationModal";
import ReviewConfirmationModal from "@/components/ReportHistory/reviewConfirmationModal";
import Select2 from '@/components/elements/select2';
import ButtonRedPrimary from "@/components/elements/buttons/ButtonRedPrimary";
import ButtonRedSecondary from "@/components/elements/buttons/ButtonRedSecondary";
import GreenButton from "@/components/elements/green-button";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";
import ButtonGreenPrimary from "@/components/elements/buttons/ButtonGreenPrimary";
import ReportHistoryDetails from "@/components/ReportHistory/reportHistoryDetails";
import ReportPreview from "@/components/ReportHistory/reportPreview";
import {Features} from "@/mixins/AuthorizationHelpers/Features";
import FeatureGate from "@/components/elements/FeatureGate.vue";

export default {
  name: "ReportHistoryIndex",
  mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin, UrlParamsMixin],
  components: {
    FeatureGate,
    ReportPreview,
    ButtonGreenPrimary,
    ButtonBlueSecondary,
    GreenButton,
    ButtonRedSecondary,
    ButtonRedPrimary,
    ReviewConfirmationModal,
    NotificationModal,
    Loading, AjaxTable, Pagination, ItemsPerPageSelect, Menu, MenuButton, MenuItem, MenuItems, Select2,
    ReportHistoryDetails
  },

  props: {
    groups: Object,
    reportTemplates: Object,
    reportTypes: Object,
    initialGroups: Array,
    initialTemplates: Array,
    initialReports: Array,
    preSelectedHistory: Number
  },
  data() {
    return {
      data: [],
      headers: [
        {
          name: 'Report Information',
          sortBy: 'reportInfo',
          show: 'sm',
          key: 'reportInfo',
        },
        {
          name: 'Report Generation Information',
          sortBy: 'reportGenInfo',
          show: 'sm',
          key: 'reportGenInfo',
        },
        {
          name: 'Reviews and Approvals',
          sortBy: '',
          show: 'md',
          key: 'signingUser',
        },
        {
          name: 'Review and Approve',
          sortBy: '',
          show: 'md',
          key: 'actions',
        },
      ],
      requestNumber: 0,
      showConfirm: false,
      reviewType: null,
      notesText: '',
      params: {},
      selectedGroups: this.initialGroups,
      selectedReportTemplates: this.initialTemplates,
      selectedReportTypes: this.initialReports,
      currentTemplateOptions: null,
      currentReportOptions: null,
      currentGroupOptions: null,
      processing: false,
      showFilters: false,
      showDetails: false,
      selectedHistory: null,
      selectedReportLink: null,
      selectedReportHistory: this.preSelectedHistory,
      site: null,
      initialTableLoad: true,
      groupsUpdated: false,
      templatesUpdated: false,
      reportsUpdated: false,
    }
  },
  created() {
    this.ajaxTable.sortBy = 'sentOn'
    this.ajaxTable.sortDesc = true
    this.currentTemplateOptions = this.updateTemplateOptions();
    this.currentReportOptions = this.updateReportOptions();
    this.currentGroupOptions = this.updateGroupOptions();
    this.updateSelectedGroup();
    this.updateSelectedTemplate();
    this.updateSelectedReport();
    this.site = this.company.getCompanyId()
    this.initialTableLoad = false;

  },
  mounted() {
  },
  methods: {
    getData() {
      this.processing = true;
      this.ajaxTable.loading = true
      this.requestNumber++
      let params = JSON.parse(JSON.stringify(this.ajaxTable))
      params.requestNumber = this.requestNumber
      params.preSelectedHistory = this.selectedReportHistory;
      params = {...this.params, ...params}
      axios.get(this.company.buildCompanyApiInternalRoute(`report-history`),
          {
            params: params,
          }
      ).then(response => {
       let data = response.data;
       //  if (data.meta.requestNumber !== `${this.requestNumber}`) {
       //    return
       //  }
        this.ajaxUpdateTableData(data); // This sends the data to the mixin for AjaxTable

        this.data = !_.isNil(data.data) ? data.data : [];
        if(this.preSelectedHistory != null || this.preSelectedHistory != ''){
          this.showDetailsPanel(this.getRowDataByReportHistoryID(this.selectedReportHistory));
        }

      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.processing = false;
        this.ajaxTable.loading = false;
      })
    },

    showConfirmModal(reviewType, reportHistoryID, reportLink, reportHistory){
      this.selectedReportLink = reportLink;
      this.selectedReportHistoryID = reportHistoryID;
      this.selectedReportHistory = reportHistory
      this.reviewType = reviewType;
      this.showConfirm = true
    },

    closeModal(){
      this.showConfirm = false;
    },

    viewReport(url){
      return window.open(url, '_blank');
    },

    getReportHistoryIDValue(){
      return this.selectedReportHistoryID;
    },

    getReviewType(){
      return this.reviewType;
    },


    updateFilters(){
      this.getData();
    },

    updateSelectedGroup(){
      if(this.selectedGroups != null && this.selectedGroups != '' && this.selectedGroups != [] && this.selectedGroups != {}){
        this.groupsUpdated = true;
        this.params['groupID'] = Object.values(this.selectedGroups);
        this.updateTemplateOptions();
        this.updateReportOptions();
        return this.selectedGroups;
      }
      // Clear the groupID param if no groups are selected
      else{
        this.params['groupID'] = '';
        // If the groups were cleared, update the template and report options
        if(this.groupsUpdated) {
          this.updateTemplateOptions();
          this.updateReportOptions();
        }
        this.groupsUpdated = false;
      }

    },

    updateSelectedTemplate(event=null){
      if(this.selectedReportTemplates != null && this.selectedReportTemplates != '' && this.selectedReportTemplates != [] && this.selectedReportTemplates != {}){
          this.updateReportOptions();
          this.updateGroupOptions();
        this.params['templateID'] = Object.values(this.selectedReportTemplates);
        this.templatesUpdated = true;
        return this.selectedReportTemplates;
      }
      // Clear the templateID param if no templates are selected
      else {
        this.params['templateID'] = '';
        // If the templates were cleared, update the report and group options
        if (this.templatesUpdated) {
          this.updateReportOptions();
          this.updateGroupOptions();
        }
        this.templatesUpdated = false;
      }
    },

    updateSelectedReport(event=null){
      if(this.selectedReportTypes != null && this.selectedReportTypes != '' && this.selectedReportTypes != [] && this.selectedReportTypes != {}){
        let reportTypes = [];
        this.selectedReportTypes.forEach( (key, value) =>{
          reportTypes.push(this.currentReportOptions[parseInt(key)]);
        });
        this.params['reportType'] = reportTypes;
        this.updateGroupOptions();
        this.updateTemplateOptions();
        this.reportsUpdated = true;
        return reportTypes;
      }
      // Clear the reportType param if no reports are selected
      else{
        // If the reports were cleared, update the template and group options
        if(this.reportsUpdated) {
          this.updateGroupOptions();
          this.updateTemplateOptions();
        }
        this.params['reportType'] = '';
        this.reportsUpdated = false;
      }

    },

    clearFilters(){
      this.params = {};
      this.selectedGroups = null;
      this.selectedReportTemplates = null;
      this.selectedReportTypes = null;
      this.selectedReportHistory = null;
      this.updateTemplateOptions();
      this.updateReportOptions();
      this.updateGroupOptions();
      this.getData();
    },

    updateTemplateOptions(){
      let options = {}
      let selectedGroup = this.selectedGroups
      let selectedTemplate = this.selectedReportTemplates;
      let selectedReport = this.selectedReportTypes;
      let selectedReportNames = this.getSelectedReportTypesPretty();
      console.log('report Templates');
      console.log(this.reportTemplates);


      let filtered = false;
      this.reportTemplates.forEach( (element) =>{
        if(selectedReport != null && selectedReport != '' && selectedReport != [] && selectedReport != {}){
          if(selectedTemplate == null || selectedTemplate == '' || selectedTemplate == [] || selectedTemplate == {}){
            filtered = true;

            this.reportTypes.forEach((reportHistory) =>{
              if(selectedReportNames.includes(reportHistory.report_type_pretty)){
                if(element.id == reportHistory.report_template_id){
                  options[element.id] = element.template_name
                }

              }
            });



          }
        }

        if(selectedGroup != null && selectedGroup != '' && selectedGroup != []){
          if(Object.values(selectedGroup).includes(element.group_id.toString())){
            let templateID = element['id'];
            let templateName = element['template_name'];
            options[templateID] = templateName;
          }
          filtered = true;
        }

        if(!filtered){
          let templateID = element['id'];
          let templateName = element['template_name'];
          options[templateID] = templateName;
        }
      });

      this.currentTemplateOptions = options;
      return this.currentTemplateOptions;
    },

    updateReportOptions(){
      let selectedGroup = this.selectedGroups;
      let selectedTemplate = this.selectedReportTemplates;
      let options = this.reportTypes;
      console.log('report types');
      console.log(this.reportTypes);

      if(selectedGroup != null && selectedGroup != '' && selectedGroup != [] && selectedGroup != {}){
        options = options.filter(function(element){
          if(element.group_id == null){
            return false;
          }
          else {
            return Object.values(selectedGroup).includes(element.group_id.toString());
          }
        });
      }
      if(selectedTemplate != null && selectedTemplate != '' && selectedTemplate != []){
        options = options.filter(function(element){
          return Object.values(selectedTemplate).includes(element.report_template_id.toString());
        });
      }
      let newObj = {};
      let nameList = [];
      // Deduplicate report types
      options.forEach((item) =>{
        if(!nameList.includes(item.report_type_pretty)){
          newObj[item.id] = item.report_type_pretty
          nameList.push(item.report_type_pretty);
        }

      });

      this.currentReportOptions = newObj;
      return this.currentReportOptions;
    },

    updateGroupOptions(){
      let selectedGroup = this.selectedGroups;
      let options = [];
      let initialFilter = true;

      console.log('updategroupoptions');
      console.log(this.selectedGroups);
      console.log(this.selectedReportTypes);
      console.log(this.selectedReportTemplates);
      console.log(this.groups);
      console.log(this.reportTypes);
      if(selectedGroup == null || selectedGroup == '' || selectedGroup == [] || selectedGroup == {}) {
        let selectedReport = this.selectedReportTypes;
        let selectedTemplate = this.selectedReportTemplates;
        if (selectedReport != null && selectedReport != '' && selectedReport != [] && selectedReport != {}) {
          initialFilter =false;
          Object.entries(this.groups).forEach((group) =>
          {
            this.getSelectedReportTypesPretty().forEach((reportName) => {
              this.reportTypes.forEach((item) => {
                if (reportName == item.report_type_pretty) {
                  if (group[0] == item.group_id) {
                    options[group[0]] = group[1];
                  }
                }
              });
            });
          });

        }
        if (selectedTemplate != null && selectedTemplate != '' && selectedTemplate != [] && selectedTemplate != {}) {
          initialFilter = false;
          Object.entries(this.groups).forEach((group) =>
          {
            this.selectedReportTemplates.forEach((key, value) => {
              this.reportTemplates.forEach((item) => {
                if (key == item.id) {
                  if (group[0] == item.group_id) {
                    options[group[0]] = group[1];
                  }
                }
              });
            });
          });
        }
        if(initialFilter){
          return this.currentGroupOptions = this.groups;
        }
        else{
          return this.currentGroupOptions = options
        }

      }
      else{
        return this.currentGroupOptions = this.groups
      }

        },



    enableFilters(){
      this.showFilters = true;
    },

    hideFilters(){
      this.showFilters = false;
    },

    showDetailsPanel(rowIndex){
      // If an invalid preselected report history is passed in, -1 will be returned.
      if(rowIndex != -1) {
        this.selectedHistory = this.data[rowIndex];
        this.showDetails = true;
      }
    },
    approveReport(){
      location.reload();
    },
    getSelectedReportTypesPretty(){
      // get the pretty report names of the selected report types

      let selectedReportNames = [];
      if(this.selectedReportTypes) {
        this.reportTypes.forEach((reportHistory) => {
          if (Object.values(this.selectedReportTypes).includes(reportHistory.id.toString())) {
            selectedReportNames.push(reportHistory.report_type_pretty);
          }
        });
      }
      return selectedReportNames;
    },
    getRowDataByReportHistoryID(reportHistoryID){
      let rowIndex = this.data.findIndex((row) => {
        return row.id == reportHistoryID;
      });
      return rowIndex;
    },

  },
  computed: {
    Features() {
      return Features
    },
    shouldShowApprovals() {
      return this.featureLevel.hasFeature(Features.REPORT_APPROVALS, this.site)
    },
  },
  watch: {
  },
}
</script>

<style scoped>

</style>